// ============================================================================
// ТОКЕНЫ БРЕНДА — единственный источник правды. Brand Book v2.0.
// ============================================================================
// Любой цвет, шрифт или толщина обводки объявляются ЗДЕСЬ и нигде больше.
//
// Отсюда читают:
//   • tailwind.config.ts — генерирует классы (bg-accent-500, text-ink-900…)
//   • lib/admin/theme.ts — тёмная тема админки
//   • компоненты, где нужен inline-стиль (SVG, письма, canvas)
//
// Почему так: раньше #2563eb, #0E172A и десяток оттенков статусов были
// раскиданы по сотням мест. Поменять цвет бренда означало пройти по всему
// проекту и что-нибудь пропустить. Теперь — одна правка здесь.
//
// ПРАВИЛО 70·20·10: ~70% нейтраль и белое, ~20% графит, ~10% синий.
// Акцент не должен заливать экран — он указывает, куда нажать.
// ============================================================================

/** Основные цвета бренда. */
export const COLOR = {
  /** Ink / Графит-навы — текст, знак, тёмные панели. */
  ink: '#0E172A',
  /** Electric Blue — действие, ссылки, выбор, акцент. */
  blue: '#1E4ED8',
  /** Electric Blue темнее — наведение. Выведен из #1E4ED8, а не взят из
   *  палитры Tailwind: #1e40af был из другого семейства и рядом с брендовым
   *  синим читался как второй цвет. */
  blueHover: '#1A44BC',
  /** Ещё темнее — нажатие. */
  blueActive: '#153594',
  /** Blue Tint — подсветка выбора, мягкие плашки. */
  blueTint: '#E9EEFF',
  /** Более плотные подложки того же синего (значения из брендбука). */
  blueTint2: '#D7E1FF',
  blueTint3: '#C9D6FF',
  white: '#FFFFFF',
} as const

/**
 * Выбранный элемент — вкладка, сегмент, период, выбранный вариант, текущая
 * страница. Один цвет выбора на всю систему (7x0CXXzj, вариант A, 23.10.2026):
 * синий текст на Blue Tint, у карточки-варианта — синяя рамка. Прежде выбранное
 * заливали графитом `bg-ink-900`, и чёрная плашка спорила с синим действием.
 * Классы: `bg-selected text-selected-text`, рамка варианта `border-selected-edge`.
 * Сторож — components/ui/selected-state.guard.test.ts.
 */
export const SELECTED = {
  bg: COLOR.blueTint,
  text: COLOR.blueActive,
  edge: COLOR.blue,
} as const

/** Нейтральная шкала (Slate). Текст, границы, фоны. */
export const SLATE = {
  900: '#0E172A',
  800: '#1E293B',
  700: '#334155',
  600: '#475569',
  500: '#64748B',
  400: '#94A3B8',
  300: '#CBD5E1',
  200: '#E2E8F0',
  100: '#F1F5F9',
  50:  '#F8FAFC',
} as const

/**
 * Статусные цвета — ровно три. Больше акцентных цветов в интерфейсе нет.
 *
 * Раньше рядом с ними жили соседние оттенки Tailwind (#ef4444, #f59e0b,
 * #22c55e, #10b981) — глазом почти незаметно, но именно из таких «почти»
 * складывается ощущение неаккуратности.
 */
export const STATUS = {
  /** Доставлено, успех, оплачено. */
  success: '#16A34A',
  /** Дедлайн, тендер открыт, требует внимания. */
  warning: '#D97706',
  /** Просрочено, ошибка, отменено. */
  danger: '#DC2626',
} as const

/** Мягкие фоны статусов (плашки, бейджи). */
export const STATUS_BG = {
  success: '#EAF7EF',
  warning: '#FDF3E7',
  danger:  '#FCECEC',
} as const

/**
 * Текст статуса НА мягкой подложке. Это не другие цвета, а те же самые,
 * затемнённые до читаемого контраста: #16A34A на #EAF7EF читается плохо.
 * Так же сделано в брендбуке (.badge.done → #15803D на ok-tint).
 */
export const STATUS_TEXT = {
  success: '#15803D',
  warning: '#B45309',
  danger:  '#B91C1C',
} as const

/**
 * Текст статуса на ТЁМНОМ фоне.
 *
 * Тот же ход, что у `STATUS_TEXT`, но в другую сторону: там цвет затемняли до
 * читаемости на светлой подложке, здесь осветляем для тёмной. Цвета те же —
 * меняется только светлота.
 *
 * ── ЗАЧЕМ ЭТО ЗДЕСЬ, А НЕ В СТИЛЯХ ────────────────────────────────────────
 * В `globals.css` тёмная тема брала соседние оттенки Tailwind: #34d399,
 * #f87171, #fbbf24. Брендбук предупреждает ровно об этом: «глазом почти
 * незаметно, но именно из таких „почти“ складывается ощущение
 * неаккуратности».
 *
 * Теперь и тёмные значения объявлены здесь — единственным источником правды,
 * как и все остальные.
 */
export const STATUS_TEXT_DARK = {
  success: '#4ADE80',
  warning: '#F0B429',
  danger:  '#F87171',
} as const

/**
 * Синий на тёмном фоне.
 *
 * Брендовый #1E4ED8 на тёмной подложке читается плохо — он для белого фона.
 * Это тот же синий, осветлённый: не второй цвет, а тот же в другой светлоте.
 */
export const BLUE_DARK = '#93B4FF' as const

/**
 * Поверхности тёмной темы.
 *
 * Берутся из `SLATE`, а не подбираются заново: в стилях жил самодельный
 * #111c2e — оттенок между 900 и 800, которого в брендбуке нет.
 *
 * Отдельной тёмной палитры у продукта НЕТ и заводить её не надо: такая уже
 * была в админке (#0f1830) и была удалена как ошибка — вторая таблица цветов
 * разъехалась с общей.
 */
export const DARK_SURFACE = {
  /** Фон страницы. */
  page:   SLATE[900],
  /** Карточки и панели: на ступень светлее фона. */
  card:   SLATE[800],
  /** Границы: светлее карточки, иначе рамки сливаются и текст висит в воздухе. */
  border: SLATE[700],
} as const

/** Типографика. Onest — весь текст. Space Grotesk — цифры. */
export const FONT = {
  /** Гарнитура бренда: заголовки, интерфейс, текст. */
  sans: 'var(--font-sans), system-ui, sans-serif',
  /**
   * Цифры: суммы, номера сделок, ID, KPI.
   * Моноширинные цифры выстраивают суммы в столбик, а не заставляют их плясать.
   */
  numeric: 'var(--font-grotesk), ui-monospace, monospace',
} as const

/** Иконки: единая линейная система (сетка 24, обводка 1.8). */
export const ICON = {
  strokeWidth: 1.8,
  size: 24,
} as const

/**
 * Скругления — шкала брендбука. Ровно эти ступени, без промежуточных:
 * из «почти одинаковых» 6/8/10 на одном экране и складывается небрежность.
 */
export const RADIUS = {
  /** Мелочь: точки, метки. */
  xs: 3,
  /** Поля, кнопки, компактные карточки. */
  md: 10,
  /** Крупные блоки. */
  lg: 12,
  /** Карточки. */
  card: 14,
  /** Самые крупные панели. */
  xl: 16,
  /** Пилюли, аватары. */
  pill: 999,
} as const

/**
 * Шкала для графиков с несколькими рядами (радар сотрудников и т.п.).
 *
 * Брендбук описывает интерфейс и про графики молчит, а там встречается задача,
 * которой в интерфейсе нет: различить N накладывающихся фигур. Вместо палитры
 * из десяти чужих цветов берём ОДИН брендовый синий в десяти ступенях —
 * тогда красный и янтарный остаются сигналами «ошибка» и «внимание», а не
 * означают «сотрудник №4».
 *
 * Порядок — от тёмного к светлому: первые ряды заметнее, что обычно совпадает
 * с сортировкой по значимости.
 */
export const CHART_BLUE_RAMP = [
  '#0B2A8F', '#153594', '#1A44BC', '#1E4ED8', '#4A72E0',
  '#7593E8', '#9FB2EF', '#C9D6FF', '#D7E1FF', '#E9EEFF',
] as const
